import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import VeratadCapture from "../../face-capture/veratad/capture";
import { uploadImageToS3 } from "../../../helpers";
import { DataContext } from "../../../context/DataContext";
import Handoff from "../../handoff";
import CompactHandoff from "../../../components/compact-handoff";
import useDeviceDetection from "../../../hooks/useDeviceDetection";
import { getDeviceSignalsUrl } from "../../../utils/edge";
import useLocale from "../../../hooks/useLocale";
import Lottie from "react-lottie-player";
import FaeVerificationAnimation from "../../../assets/fae-verification.json";
import {
  Text,
  Button,
  LoadingOverlay,
  Center,
  Stack,
  Group,
  Paper,
  Box,
  ThemeIcon,
  Alert,
  Loader,
  useMantineTheme,
} from "@mantine/core";
import {
  IconChefHatOff,
  IconEyeglass2,
  IconFaceId,
  IconSunHigh,
  IconShieldX,
} from "@tabler/icons-react";
import useStatusCheck from "../../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../../hooks/useEdgeStatusCheck";
import posthog from "posthog-js";

// Loading screen component with staged text animations
const AgeEstimationLoadingScreen = ({ locale }) => {
  const [stage, setStage] = useState(0);
  const [showSpinner, setShowSpinner] = useState(false);

  const stages = [
    {
      title: locale?.processing?.uploading || "Uploading...",
      subtitle: locale?.processing?.uploadingSubtitle || "Sending your image for verification"
    },
    {
      title: locale?.processing?.verifying || "Verification in Progress",
      subtitle: locale?.processing?.verifyingSubtitle || "Analyzing your image..."
    },
    {
      title: locale?.processing?.completing || "Completing Verification",
      subtitle: locale?.processing?.completingSubtitle || "Almost done..."
    }
  ];

  useEffect(() => {
    // Stage 1 -> Stage 2 after 2 seconds
    const timer1 = setTimeout(() => {
      setStage(1);
    }, 2000);

    // Stage 2 -> Stage 3 after 4 seconds total
    const timer2 = setTimeout(() => {
      setStage(2);
    }, 4000);

    // Show spinner after 6.5 seconds (when Lottie finishes)
    const timer3 = setTimeout(() => {
      setShowSpinner(true);
    }, 6500);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        minHeight: "400px",
        padding: "20px",
      }}
    >
      {showSpinner ? (
        <Box
          style={{
            width: 300,
            height: 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: "fadeIn 0.5s ease-in-out",
          }}
        >
          <Loader size={60} />
        </Box>
      ) : (
        <Lottie
          animationData={FaeVerificationAnimation}
          loop={false}
          play
          style={{ width: 300, height: 300 }}
        />
      )}
      <Box
        style={{
          minHeight: "80px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text
          fz={24}
          fw={700}
          align="center"
          mt="md"
          style={{
            transition: "opacity 0.5s ease-in-out",
            animation: "fadeIn 0.5s ease-in-out",
          }}
          key={stage}
        >
          {stages[stage].title}
        </Text>
        <Text
          fz="md"
          c="dimmed"
          align="center"
          mt="xs"
          style={{
            transition: "opacity 0.5s ease-in-out",
            animation: "fadeIn 0.5s ease-in-out",
          }}
          key={`subtitle-${stage}`}
        >
          {stages[stage].subtitle}
        </Text>
      </Box>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </Box>
  );
};

const AgeEstimation = () => {
  const { settings, data, dispatch, text, postPayloadMessage } = useContext(DataContext);
  const theme = useMantineTheme();
  const locale = useLocale("age-estimation");
  const { isTrulyMobile, isEmulated, serverTamperDetected, serverCheckPending } = useDeviceDetection({
    requestId: settings?.request_id,
    serverReportUrl: getDeviceSignalsUrl(),
  });
  const blockedLocale = useLocale("device-blocked");
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isReadyForStatusCheck, setIsReadyForStatusCheck] = useState(false);
  const loadingStartTimeRef = useRef(null);
  const [hasError, setHasError] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [status, setStatus] = useState("NEW");
  const [onMobile, setOnMobile] = useState(isTrulyMobile);
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);
  const [qrScanned, setQrScanned] = useState(false);
  const resolvedPrimaryColor = data?.styles?.primaryColor ?? theme.primaryColor;
  const should_show_continue_on_desktop = settings?.shouldShowContinueOnDesktop === true;
  const isHandoffMode = !onMobile && !should_show_continue_on_desktop;

  // Update onMobile when device detection changes (e.g., emulation toggled)
  useEffect(() => {
    setOnMobile(isTrulyMobile);
  }, [isTrulyMobile]);

  useEffect(() => {
    if (data?.isReactNative) {
      setOnMobile(true);
    }
  }, [data?.isReactNative]);

  // Fire page shown event on mount (non-handoff users who see the intro screen)
  const pageShownFiredRef = useRef(false);
  useEffect(() => {
    if (isHandoffMode || pageShownFiredRef.current) return;
    pageShownFiredRef.current = true;
    if (posthog?.capture) {
      posthog.capture("face_capture_page_shown", {
        request_id: settings.request_id,
        provider: "veratad",
      });
    }
  }, []);

  // Fire handoff event when desktop user sees QR handoff screen
  const handoffEventFiredRef = useRef(false);
  useEffect(() => {
    if (!isHandoffMode || handoffEventFiredRef.current) return;
    handoffEventFiredRef.current = true;
    if (posthog?.capture) {
      posthog.capture("face_capture_handoff_shown", {
        request_id: settings.request_id,
      });
    }
  }, []);

  // Fire camera allowed event when user grants camera permission
  const cameraAllowedFiredRef = useRef(false);
  const handleCameraAllowed = useCallback(() => {
    if (cameraAllowedFiredRef.current) return;
    cameraAllowedFiredRef.current = true;
    if (posthog?.capture) {
      posthog.capture("face_capture_camera_allowed", {
        request_id: settings.request_id,
        provider: "veratad",
      });
    }
  }, [settings.request_id]);

  // Handler for verification result from edge status check
  const verificationEventFiredRef = useRef(false);
  const handleVerificationResult = useCallback((status) => {
    if (verificationEventFiredRef.current) return;
    verificationEventFiredRef.current = true;
    if (posthog?.capture) {
      posthog.capture("face_capture_verification_result", {
        request_id: settings.request_id,
        provider: "veratad",
        result: status,
      });
    }
  }, [settings.request_id]);

  // Delay the first status check until the loading animation is finishing up

  // Use edge status check if edge mode is enabled
  if (data?.edge === true) {
    useEdgeStatusCheck(isReadyForStatusCheck, null, false, handleVerificationResult);
  } else {
    useStatusCheck(isReadyForStatusCheck, false, "agematch/estimate");
  }

  const handleLoading = () => {
    loadingStartTimeRef.current = Date.now();
    setIsLoading(true);
  };

  const handleComplete = () => {
    const request_id = settings.request_id;
    data.request_id = request_id;
    data.type = "SDK_SCAN";
    data.country = settings.country;
    if (!("front_ext" in data)) {
      data.front_ext = "jpeg";
      data.back_ext = "jpeg";
    }
    setIsComplete(true);

    // Delay status check until ~5s after the loading animation started
    const elapsed = Date.now() - (loadingStartTimeRef.current || Date.now());
    const remainingDelay = Math.max(0, 5000 - elapsed);
    setTimeout(() => {
      setIsReadyForStatusCheck(true);
    }, remainingDelay);

    // Send postMessage that verification has been initiated
    if (postPayloadMessage) {
      postPayloadMessage({
        event: "VERIFICATION_INITIATED",
        app: "agematch/estimate",
      });
    }

    // Check if edge mode is enabled
    const edgeQueueUrl =
      process.env.REACT_APP_EDGE_QUEUE_URL ||
      "https://dcams.app/edge/frontend/api/queue/batch";

    const endpoint = data?.edge === true
      ? edgeQueueUrl
      : process.env.REACT_APP_BACKEND_URL + "/v3/stable/api/frontend/estimate/process.php";

    // Add queue parameter for edge mode
    const requestBody = data?.edge === true
      ? { ...data, queue: "estimate" }
      : data;

    fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {})
      .catch(function () {
        alert("Something Went Wrong! Please try again.");
      });
    if (posthog?.capture) {
      posthog.capture("face_capture_completed", {
        request_id: settings.request_id,
        provider: "veratad",
      });
    }
    // Additional code if needed when the image processing is complete
  };

  const handleError = (error) => {
    setHasError(true);
    dispatch({
      type: "SET_IS_CURRENT_COMPONENT_COMPLETE",
      payload: true,
    });
    dispatch({ type: "SET_STATUS", payload: "PENDING" });
    dispatch({ type: "TRIGGER_NAVIGATION", payload: true });

    if (posthog?.capture) {
      const baseProps = {
        request_id: settings.request_id,
        provider: "veratad",
      };

      // Fire granular event based on error type
      if (error?.type === "camera_error") {
        posthog.capture("face_capture_camera_error", {
          ...baseProps,
          error_name: error.name,
          error_message: error.message,
        });
      } else if (error?.type === "upload_error") {
        posthog.capture("face_capture_upload_failed", {
          ...baseProps,
          error_message: error.message,
        });
      } else if (error?.type === "model_load_error") {
        posthog.capture("face_capture_model_load_failed", {
          ...baseProps,
          error_message: error.message,
        });
      }

      // Always fire the catch-all with a reason for backwards compatibility
      posthog.capture("face_capture_failed", {
        ...baseProps,
        reason: error?.type || "unknown",
        error_message: error?.message || undefined,
      });
    }
  };

  // Handler for Veratad capture — uploads to S3 then triggers the same flow as Yoti
  const handleVeratadCapture = useCallback(
    async (base64Image) => {
      handleLoading();
      try {
        const response = await fetch(base64Image);
        const blob = await response.blob();
        const imageKey = `${settings.request_id}_dl_selfie.jpeg`;
        await uploadImageToS3(imageKey, blob, settings);
        handleComplete();
      } catch (err) {
        console.error("Veratad capture upload failed:", err);
        handleError({ type: "upload_error", message: err?.message || "Image upload failed" });
      }
    },
    [settings]
  );

  const handleVeratadReady = useCallback(() => {
    setIsReady(true);
  }, []);

  const handleStart = () => {
    // Send postMessage for get started click
    if (postPayloadMessage) {
      postPayloadMessage({
        event: "GET_STARTED_CLICKED",
        app: "agematch/estimate",
      });
    }

    if (posthog?.capture) {
      posthog.capture("face_capture_started", {
        request_id: settings.request_id,
        provider: "veratad",
      });
    }

    setHasStarted(true);
  };

  // Conditional banner logic
  const shouldShowBanner = (settings.site_name === "kid-pre-prod" || settings.site_name === "kid-qa") && 
                           settings.journey?.name && 
                           (settings.journey.name === "estimate_only_roc" || settings.journey.name === "estimate_only_yoti");
  
  const getBannerText = () => {
    if (settings.journey?.name === "estimate_only_roc") return "FAE PROVIDER: ROC";
    if (settings.journey?.name === "estimate_only_yoti") return "FAE PROVIDER: Yoti";
    return "";
  };

  const intro = (
    <>
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between", // This will push the button to the bottom
          height: "100%", // This should take the full height of its parent
          position: "relative",
        }}
      >
        {/**
         * Conditional banner commented out as the banner should no longer
         * display for kid-pre-prod or kid-qa while preserving the code for
         * potential future use.
         */}
        {/*shouldShowBanner && !isBannerDismissed && (
          <Alert
            color="blue"
            withCloseButton
            onClose={() => setIsBannerDismissed(true)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1000,
              borderRadius: 0,
              padding: "8px",
              minHeight: "auto"
            }}
          >
            <Text align="center" fw={600} size="sm">
              {getBannerText()}
            </Text>
          </Alert>
        )*/}
        
        <Box>
          <Stack align="center" spacing={(!onMobile && should_show_continue_on_desktop) ? "sm" : "md"}>
            <ThemeIcon
              variant="light"
              c={resolvedPrimaryColor}
              size={(!onMobile && should_show_continue_on_desktop) ? 60 : 85}
              radius={"xl"}
            >
              <IconFaceId
                size={(!onMobile && should_show_continue_on_desktop) ? 45 : 70}
                stroke={1}
                color={
                  theme.colorScheme === "dark"
                    ? theme.white
                    : theme.fn.variant({ variant: "filled", color: resolvedPrimaryColor }).background
                }
              />
            </ThemeIcon>
            <Text fz={(!onMobile && should_show_continue_on_desktop) ? 24 : 30} fw={700} align="center">
              {text.estimation.title}
            </Text>
          </Stack>
          <Text align="center" fz={(!onMobile && should_show_continue_on_desktop) ? "sm" : undefined}>{text.estimation.subTitle}</Text>
          <Paper
            withBorder
            m="md"
            p={(!onMobile && should_show_continue_on_desktop) ? "sm" : "md"}
            style={{
              backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : "#fafafa"
            }}
          >
            <Stack spacing={(!onMobile && should_show_continue_on_desktop) ? "md" : "xl"}>
              <Group noWrap>
                <IconSunHigh size={(!onMobile && should_show_continue_on_desktop) ? 20 : 30} />
                <Text fz={(!onMobile && should_show_continue_on_desktop) ? 14 : 18}>{text.estimation.instructions[0]}</Text>
              </Group>
              <Group noWrap>
                <IconChefHatOff size={(!onMobile && should_show_continue_on_desktop) ? 20 : 30} />
                <Text fz={(!onMobile && should_show_continue_on_desktop) ? 14 : 18}>{text.estimation.instructions[1]}</Text>
              </Group>
              <Group noWrap>
                <IconEyeglass2 size={(!onMobile && should_show_continue_on_desktop) ? 20 : 30} />
                <Text fz={(!onMobile && should_show_continue_on_desktop) ? 14 : 18}>{text.estimation.instructions[2]}</Text>
              </Group>
            </Stack>
          </Paper>
        </Box>
        <Box>
          <Text fz="sm" c="dimmed" align="center" pb={(!onMobile && should_show_continue_on_desktop) ? 8 : 15}>
            {text.estimation.note}
          </Text>
          <Button
            size="lg"
            onClick={handleStart}
            fullWidth
            disabled={qrScanned}
          >
            {text.estimation.button}
          </Button>

          {/* Show compact handoff QR when continue on desktop is enabled and not on mobile */}
          {!onMobile && should_show_continue_on_desktop && (
            <CompactHandoff
              nextPath="agematch/estimate"
              onQrScanned={setQrScanned}
            />
          )}
        </Box>
      </Box>
    </>
  );

  // Show loading overlay while server-side device check is pending
  if (serverCheckPending) {
    return <LoadingOverlay visible />;
  }

  // Block session if server-side tamper detection flagged this request
  if (serverTamperDetected) {
    return (
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: "400px",
          padding: "20px",
        }}
      >
        <ThemeIcon variant="light" color="red" size={80} radius="xl">
          <IconShieldX size={50} stroke={1.5} />
        </ThemeIcon>
        <Text fz={24} fw={700} align="center" mt="lg">
          {blockedLocale?.blocked?.title || "Unable to Continue"}
        </Text>
        <Text fz="md" c="dimmed" align="center" mt="xs" maw={400}>
          {blockedLocale?.blocked?.message || "Please try again on your mobile device."}
        </Text>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <AgeEstimationLoadingScreen locale={locale} />
    );
  }

  if (hasStarted) {
    return (
      <VeratadCapture
        onCapture={handleVeratadCapture}
        onError={handleError}
        onReady={handleVeratadReady}
        onCameraAllowed={handleCameraAllowed}
      />
    );
  }

  // Show standard handoff if not on mobile and shouldShowContinueOnDesktop is false
  if (!onMobile && !should_show_continue_on_desktop) {
    return <Handoff nextPath="agematch/estimate" />;
  }

  return intro;
};

export default AgeEstimation;
